import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Command, CommandInput, CommandList, CommandGroup, CommandItem, CommandEmpty } from "./ui/command";
import { Dialog, DialogContent } from "./ui/dialog";
import { Users, Home, Target, FileSignature, CheckSquare } from "lucide-react";
import api from "../lib/api";

const ICONS = { contact: Users, property: Home, opportunity: Target, transaction: FileSignature, task: CheckSquare };
const ROUTES = { contact: "/contacts/", property: "/properties/", opportunity: "/pipeline", transaction: "/transactions", task: "/tasks" };

export default function GlobalSearch({ open, onClose }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const nav = useNavigate();
  const timer = useRef();

  useEffect(() => {
    if (!q) { setResults([]); return; }
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      try {
        const { data } = await api.get(`/search?q=${encodeURIComponent(q)}`);
        setResults(data.results);
      } catch {}
    }, 200);
  }, [q]);

  const go = (r) => {
    onClose();
    setQ("");
    if (r.type === "contact") nav(`/contacts/${r.id}`);
    else if (r.type === "property") nav(`/properties/${r.id}`);
    else nav(ROUTES[r.type] || "/");
  };

  const grouped = results.reduce((acc, r) => {
    (acc[r.type] = acc[r.type] || []).push(r);
    return acc;
  }, {});

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="p-0 overflow-hidden max-w-xl gap-0" data-testid="global-search-dialog">
        <Command shouldFilter={false} className="bg-white">
          <CommandInput value={q} onValueChange={setQ} placeholder="Search contacts, properties, deals, tasks…" data-testid="global-search-input" />
          <CommandList>
            {q && results.length === 0 && <CommandEmpty>No results for "{q}"</CommandEmpty>}
            {Object.entries(grouped).map(([type, items]) => {
              const Icon = ICONS[type] || Users;
              return (
                <CommandGroup key={type} heading={type.toUpperCase() + "S"}>
                  {items.map((r) => (
                    <CommandItem key={r.id} onSelect={() => go(r)} value={r.id} className="cursor-pointer" data-testid={`search-result-${r.type}`}>
                      <Icon className="w-4 h-4 mr-2 text-slate-400" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{r.title}</p>
                        <p className="text-xs text-slate-400 truncate">{r.subtitle}</p>
                      </div>
                      {r.meta && <span className="text-[10px] uppercase tracking-wide text-slate-400 ml-2">{r.meta}</span>}
                    </CommandItem>
                  ))}
                </CommandGroup>
              );
            })}
          </CommandList>
        </Command>
      </DialogContent>
    </Dialog>
  );
}
